/**
 * Plays a recording into a kiosk, so the screen shows exactly what the user saw.
 *
 * Seeking is replay from the start: the kiosk is cleared and every step up to the target is applied
 * again. Simple and exact — the state at step N is, by construction, what the live kiosk had.
 */
import type { Kiosk } from '../core/kiosk';
import { createStore, type Store } from '../core/store';
import type { Recording } from './recording';

export interface PlayerState {
  /** Steps applied so far (0 = nothing yet, steps.length = the end). */
  position: number;
  playing: boolean;
  speed: number;
}

export interface ReplayPlayer {
  recording: Recording;
  state: Store<PlayerState>;
  play(): void;
  pause(): void;
  setSpeed(speed: number): void;
  seek(position: number): void;
  /** To the first step of a turn (index into recording.turns), showing what it produced. */
  toTurn(index: number): void;
  nextTurn(): void;
  previousTurn(): void;
  /** The turn whose events were applied last, or null before the first one. */
  currentTurn(): number | null;
  dispose(): void;
}

export const SPEEDS = [1, 2, 4, 10] as const;
/** Long silences in a recording are shortened to this, so playback never stalls. */
const MAX_WAIT_MS = 2500;

/**
 * The turn under way at a position: the last one that started before it. A pure function of the
 * position, so a component reading the player's state gets it fresh (a method call on the player
 * would be memoized by the React Compiler).
 */
export function turnAt(recording: Recording, position: number): number | null {
  let current: number | null = null;
  for (const t of recording.turns) if (t.firstStep < position) current = t.index;
  return current;
}

export function createReplayPlayer(recording: Recording, kiosk: Kiosk): ReplayPlayer {
  const state = createStore<PlayerState>('replay', { position: 0, playing: false, speed: 1 });
  let timer: ReturnType<typeof setTimeout> | null = null;

  const applyUpTo = (target: number) => {
    const from = state.get().position;
    if (target < from) {
      kiosk.clear();
      recording.steps.slice(0, target).forEach((s) => kiosk.apply(s.event));
    } else {
      recording.steps.slice(from, target).forEach((s) => kiosk.apply(s.event));
    }
    state.set((s) => ({ ...s, position: target }));
  };

  const stopTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const scheduleNext = () => {
    stopTimer();
    const { position, playing, speed } = state.get();
    if (!playing) return;
    if (position >= recording.steps.length) {
      state.set((s) => ({ ...s, playing: false }));
      return;
    }
    const previousAt = recording.steps[position - 1]?.at ?? recording.steps[0]?.at ?? 0;
    const gapMs = Math.max(0, (recording.steps[position]!.at - previousAt) * 1000);
    timer = setTimeout(() => {
      applyUpTo(position + 1);
      scheduleNext();
    }, Math.min(gapMs, MAX_WAIT_MS) / speed);
  };

  const clamp = (p: number) => Math.max(0, Math.min(recording.steps.length, p));

  /** The end of a turn: its first step plus all its events, up to the next turn. */
  const endOfTurn = (index: number) => recording.turns[index + 1]?.firstStep ?? recording.steps.length;

  const player: ReplayPlayer = {
    recording,
    state,
    play() {
      if (state.get().position >= recording.steps.length) applyUpTo(0);
      state.set((s) => ({ ...s, playing: true }));
      scheduleNext();
    },
    pause() {
      stopTimer();
      state.set((s) => ({ ...s, playing: false }));
    },
    setSpeed(speed) {
      state.set((s) => ({ ...s, speed }));
      scheduleNext();
    },
    seek(position) {
      applyUpTo(clamp(position));
      scheduleNext();
    },
    toTurn(index) {
      if (index < 0 || index >= recording.turns.length) return;
      player.seek(endOfTurn(index));
    },
    nextTurn() {
      const current = player.currentTurn();
      player.toTurn(current === null ? 0 : current + 1);
    },
    previousTurn() {
      const current = player.currentTurn();
      if (current === null || current === 0) player.seek(0);
      else player.toTurn(current - 1);
    },
    currentTurn() {
      return turnAt(recording, state.get().position);
    },
    dispose: stopTimer,
  };

  // Open on the first turn done: before it there is no session to show.
  if (recording.turns.length > 0) player.toTurn(0);
  return player;
}
